import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  ClipboardDocumentListIcon, 
  DocumentChartBarIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  BuildingOfficeIcon,
  HomeIcon,
  ArchiveBoxIcon,
  UserGroupIcon,
  MapPinIcon,
  QueueListIcon,
  CalendarIcon,
  ShoppingCartIcon,
  ChartBarIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CubeIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import Notifications from '../Notifications';
import { getTaskStats, getUnreadNotificationCount } from '../../utils/api';
import { ToastContainer } from '../UI/Toast';
import { useToast } from '../../contexts/ToastContext';
import { trackDemoStartForFarmClicked } from '../../analytics';

// Role-based navigation arrays
const userNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Data Entry', href: '/data-entry/', icon: ClipboardDocumentListIcon },
  { name: 'Retail Sales', href: '/retail-sales', icon: CurrencyDollarIcon },
  { name: 'Logs', icon: QueueListIcon, submenu: [
    { name: 'Feed', href: '/feed-logs' },
    { name: 'Medicine', href: '/medicine-logs' },
    { name: 'Water Quality', href: '/water-quality-logs' },
    { name: 'Fish Seed', href: '/fish-seed-logs' },
    { name: 'Sampling', href: '/fish-sampling-logs' },
    { name: 'Dead Fish', href: '/dead-fish-logs' },
  ]},
  { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Assets', href: '/assets', icon: CubeIcon },
];

// Manager has same navigation as User (no Reports, no Cycle Planning) + Payment Requests + Payroll + Tasks
// Filter out Payroll and Assets from userNavigation to avoid duplication, then add them at the end
const managerNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon },
  ...userNavigation.filter(item => item.name !== 'Dashboard' && item.name !== 'Payroll' && item.name !== 'Assets'),
  { name: 'Payment Requests', href: '/payment-requests', icon: BanknotesIcon },
  { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Assets', href: '/assets', icon: CubeIcon },
];

const adminNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon, submenu: [
    { name: 'All Tasks', href: '/tasks' },
    { name: 'Templates', href: '/tasks/templates' },
  ]},
  { name: 'Data Entry', href: '/data-entry/', icon: ClipboardDocumentListIcon },
  { name: 'Retail Sales', href: '/retail-sales', icon: CurrencyDollarIcon },
  { name: 'Logs', icon: QueueListIcon, submenu: [
    { name: 'Feed', href: '/feed-logs' },
    { name: 'Medicine', href: '/medicine-logs' },
    { name: 'Water Quality', href: '/water-quality-logs' },
    { name: 'Fish Seed', href: '/fish-seed-logs' },
    { name: 'Sampling', href: '/fish-sampling-logs' },
    { name: 'Dead Fish', href: '/dead-fish-logs' },
  ]},
  { name: 'Payment Requests', href: '/payment-requests', icon: BanknotesIcon },
  { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Inventory', href: '/reports/inventory-snapshot', icon: ArchiveBoxIcon },
  { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
  { name: 'Cycle Planning', href: '/cycle-planning', icon: CalendarIcon },
  { name: 'Assets', href: '/assets', icon: CubeIcon },
];

// Goat-specific navigation
const goatUserNavigation = [
  { name: 'Dashboard', href: '/goat/dashboard', icon: HomeIcon },
  { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon },
  { name: 'Data Entry', href: '/goat/data-entry/', icon: ClipboardDocumentListIcon },
  { name: 'Goat Data', href: '/goat/master', icon: BuildingOfficeIcon },
  { name: 'Sheds', href: '/goat/sheds', icon: BuildingOfficeIcon },
  { name: 'Goat Logs', href: '/goat/logs', icon: QueueListIcon },
  { name: 'Payment Requests', href: '/payment-requests', icon: BanknotesIcon },
  { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Assets', href: '/assets', icon: CubeIcon },
];

const goatAdminNavigation = [
  { name: 'Dashboard', href: '/goat/dashboard', icon: HomeIcon },
  { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentCheckIcon, submenu: [
    { name: 'All Tasks', href: '/tasks' },
    { name: 'Templates', href: '/tasks/templates' },
  ]},
  { name: 'Data Entry', href: '/goat/data-entry/', icon: ClipboardDocumentListIcon },
  { name: 'Goat Data', href: '/goat/master', icon: BuildingOfficeIcon },
  { name: 'Sheds', href: '/goat/sheds', icon: BuildingOfficeIcon },
  { name: 'Goat Logs', href: '/goat/logs', icon: QueueListIcon },
  { name: 'Payment Requests', href: '/payment-requests', icon: BanknotesIcon },
  { name: 'Payroll', href: '/payroll', icon: CurrencyDollarIcon },
  { name: 'Goat Report', href: '/goat/reports', icon: DocumentChartBarIcon },
  { name: 'Assets', href: '/assets', icon: CubeIcon },
];

const adminPanelNavigation = [
  { name: 'Admin Panel', href: '/admin', icon: CogIcon },
  { name: 'Manage Locations', href: '/admin/locations', icon: BuildingOfficeIcon },
  { name: 'Manage Users', href: '/admin/users', icon: UserGroupIcon },
];

// Manager panel navigation - inventory access
const managerPanelNavigation = [
  { name: 'Inventory Purchases', href: '/admin/inventory/purchases', icon: ShoppingCartIcon },
  { name: 'Inventory Balances', href: '/admin/inventory/balances', icon: ChartBarIcon },
];

// Navigation helper function
const getNavigationForRole = (role: string, businessType: 'fish' | 'goat' = 'fish') => {
  // For goat farming, use goat-specific navigation
  if (businessType === 'goat') {
    if (role === 'admin') return goatAdminNavigation;
    // Managers and users both get goatUserNavigation (which includes Tasks)
    return goatUserNavigation;
  }
  
  // For fish farming, use fish navigation
  let nav = [];
  if (role === 'admin') nav = adminNavigation;
  else if (role === 'manager') nav = managerNavigation;
  else nav = userNavigation;
  
  return nav;
};

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedLocation, setSelectedLocation, setSelectedLocationOptimistic, loading: locationLoading, isInitialized } = useLocationContext();
  const { businessType, setBusinessType, hasMultipleTypes } = useBusinessTypeContext();
  const [taskCount, setTaskCount] = useState<number | null>(null);
  const { toasts, removeToast } = useToast();
  const DEMO_ADMIN_ID = process.env.REACT_APP_DEMO_ADMIN_ID || 'demo-admin';
  const isDemoUser = user?.id === DEMO_ADMIN_ID;

  // Redirect to location selection if no location is selected (but not while loading)
  // If we navigated from LocationSelection with state.selectedLocation, set it optimistically (no API call) to avoid 403 loop
  useEffect(() => {
    const stateLocation = (location.state as { selectedLocation?: { id: string } } | null)?.selectedLocation;
    if (stateLocation && !selectedLocation) {
      setSelectedLocationOptimistic(stateLocation as any);
      return;
    }
    const isAdminRoute = location.pathname.startsWith('/admin');
    if (isInitialized && !locationLoading && !selectedLocation && location.pathname !== '/locations' && !isAdminRoute) {
      navigate('/locations');
    }
  }, [selectedLocation, locationLoading, location.pathname, location.state, navigate, isInitialized, setSelectedLocationOptimistic]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Load notification count for badge (use notification count instead of task stats for consistency)
  useEffect(() => {
    if (selectedLocation?.id && (user?.role === 'admin' || user?.role === 'manager')) {
      const loadNotificationCount = async () => {
        try {
          const data = await getUnreadNotificationCount();
          setTaskCount(data.unread_count);
        } catch (error) {
          console.error('Error loading notification count:', error);
          // Fallback to task stats if notification count fails
          try {
            const stats = await getTaskStats(selectedLocation.id);
            setTaskCount(stats.pending_count + stats.overdue_count);
          } catch (statsError) {
            console.error('Error loading task stats:', statsError);
            setTaskCount(null);
          }
        }
      };
      loadNotificationCount();
      const interval = setInterval(loadNotificationCount, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    } else {
      setTaskCount(null);
    }
  }, [selectedLocation?.id, user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handleStartForFarm = () => {
    trackDemoStartForFarmClicked();
    logout();
    // Use hard navigation so auth redirect guards cannot strip query params.
    window.location.assign('/auth?from=demo');
  };

  const handleLocationChange = () => {
    setSelectedLocation(null);
    navigate('/locations');
  };

  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-80 max-w-[85vw] flex-col bg-white shadow-2xl">
          <div className="flex h-16 items-center justify-between px-4 border-b border-gray-200 bg-white">
            <div className="flex items-center space-x-2">
              <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="16" fill="#2563eb"/>
                <ellipse cx="16" cy="16" rx="8" ry="5" fill="#ffffff"/>
                <path d="M8 16 L4 12 L4 20 Z" fill="#ffffff"/>
                <circle cx="20" cy="14" r="1.5" fill="#2563eb"/>
                <path d="M6 24 Q8 22 10 24 Q12 26 14 24 Q16 22 18 24 Q20 26 22 24 Q24 22 26 24" stroke="#ffffff" strokeWidth="1" fill="none" opacity="0.7"/>
              </svg>
              <h2 className="text-lg font-bold text-gray-900">FarmLog</h2>
            </div>
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Location info */}
          {selectedLocation && (
            <div className="px-4 py-4 bg-gradient-to-r from-blue-50 to-green-50 border-b border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <MapPinIcon className="h-5 w-5 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">{selectedLocation.name}</p>
              </div>
              <button 
                onClick={handleLocationChange}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium mb-3 block"
              >
                Change Location →
              </button>
              
              {/* Business Type Toggle in Mobile Sidebar */}
              {hasMultipleTypes && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Business Type</p>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setBusinessType('fish');
                        setSidebarOpen(false);
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        businessType === 'fish'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      🐟 Fish
                    </button>
                    <button
                      onClick={() => {
                        setBusinessType('goat');
                        setSidebarOpen(false);
                      }}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        businessType === 'goat'
                          ? 'bg-green-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      🐐 Goat
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {/* Main Navigation */}
            <div className="space-y-1">
              <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Main Menu
              </h3>
              {getNavigationForRole(user?.role || 'user', businessType).map((item) => {
                const isActive = item.href ? (location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href))) : false;
                
                // Handle submenu items
                if ('submenu' in item && item.submenu) {
                  return (
                    <div key={item.name} className="space-y-1">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {item.name}
                      </div>
                      {item.submenu.map((subItem) => {
                        const isSubActive = location.pathname === subItem.href;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`group flex items-center px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                              isSubActive
                                ? 'bg-blue-100 text-blue-900'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-900 border-l-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <div className="relative mr-3">
                      <item.icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name === 'Tasks' && taskCount !== null && taskCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {taskCount > 9 ? '9+' : taskCount}
                        </span>
                      )}
                    </div>
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Admin Navigation */}
            {user?.role === 'admin' && (
              <div className="space-y-1 pt-4 border-t border-gray-200">
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Administration
                </h3>
                {adminPanelNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-100 text-purple-900 border-l-4 border-purple-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Inventory Management - accessible to both admin and manager */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div className="space-y-1 pt-4 border-t border-gray-200">
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Inventory Management
                </h3>
                {managerPanelNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-teal-100 text-teal-900 border-l-4 border-teal-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-teal-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
            
            {/* Logout */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="w-full group flex items-center px-3 py-3 text-sm font-medium rounded-lg text-red-700 hover:bg-red-50 hover:text-red-900 transition-colors"
              >
                <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5 text-red-500" />
                Sign Out
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-gray-200 shadow-sm">
          <div className="flex h-16 flex-shrink-0 items-center px-6 border-b border-gray-200 bg-white">
            <div className="flex items-center space-x-3">
              <svg className="h-8 w-8 text-blue-600" fill="currentColor" viewBox="0 0 32 32">
                <circle cx="16" cy="16" r="16" fill="#2563eb"/>
                <ellipse cx="16" cy="16" rx="8" ry="5" fill="#ffffff"/>
                <path d="M8 16 L4 12 L4 20 Z" fill="#ffffff"/>
                <circle cx="20" cy="14" r="1.5" fill="#2563eb"/>
                <path d="M6 24 Q8 22 10 24 Q12 26 14 24 Q16 22 18 24 Q20 26 22 24 Q24 22 26 24" stroke="#ffffff" strokeWidth="1" fill="none" opacity="0.7"/>
              </svg>
              <h1 className="text-xl font-bold text-gray-900">FarmLog</h1>
            </div>
          </div>
          
          {/* Location info */}
          {selectedLocation && (
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-green-50 border-b border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <MapPinIcon className="h-5 w-5 text-blue-600" />
                <p className="text-sm font-semibold text-blue-900">{selectedLocation.name}</p>
              </div>
              <button 
                onClick={handleLocationChange}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Change Location →
              </button>
            </div>
          )}
          
          <nav className="flex-1 space-y-1 px-4 py-6 overflow-y-auto">
            {/* Main Navigation */}
            <div className="space-y-1">
              <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Main Menu
              </h3>
              {getNavigationForRole(user?.role || 'user', businessType).map((item) => {
                const isActive = item.href ? (location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href))) : false;
                
                // Handle submenu items
                if ('submenu' in item && item.submenu) {
                  const isParentActive = item.href ? (location.pathname === item.href || 
                    (item.href !== '/dashboard' && location.pathname.startsWith(item.href))) : false;
                  return (
                    <div key={item.name} className="space-y-1">
                      <Link
                        to={item.href || '#'}
                        className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                          isParentActive
                            ? 'bg-blue-100 text-blue-900 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <div className="relative mr-3">
                          <item.icon className={`h-5 w-5 ${isParentActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                          {item.name === 'Tasks' && taskCount !== null && taskCount > 0 && (
                            <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                              {taskCount > 9 ? '9+' : taskCount}
                            </span>
                          )}
                        </div>
                        {item.name}
                      </Link>
                      {item.submenu.map((subItem) => {
                        const isSubActive = location.pathname === subItem.href;
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className={`group flex items-center px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                              isSubActive
                                ? 'bg-blue-100 text-blue-900'
                                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-900 border-l-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <div className="relative mr-3">
                      <item.icon className={`h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name === 'Tasks' && taskCount !== null && taskCount > 0 && (
                        <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {taskCount > 9 ? '9+' : taskCount}
                        </span>
                      )}
                    </div>
                    {item.name}
                  </Link>
                );
              })}
            </div>

            {/* Admin Navigation */}
            {user?.role === 'admin' && (
              <div className="space-y-1 pt-6 border-t border-gray-200">
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Administration
                </h3>
                {adminPanelNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-purple-100 text-purple-900 border-l-4 border-purple-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-purple-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Inventory Management - accessible to both admin and manager */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div className="space-y-1 pt-6 border-t border-gray-200">
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Inventory Management
                </h3>
                {managerPanelNavigation.map((item) => {
                  const isActive = location.pathname.startsWith(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`group flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActive
                          ? 'bg-teal-100 text-teal-900 border-l-4 border-teal-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className={`mr-3 h-5 w-5 ${isActive ? 'text-teal-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>
          
          {/* User info and logout */}
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="flex items-center space-x-3 mb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white font-semibold text-sm">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <ArrowLeftOnRectangleIcon className="mr-2 h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-4">
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              {/* Breadcrumb for mobile */}
              <div className="lg:hidden">
                <h1 className="text-lg font-semibold text-gray-900">
                  {isAdminRoute ? 'Administration' : 'FarmLog.in'}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Location indicator - hidden on mobile, shown on sm+ */}
              {selectedLocation && (
                <div className="hidden sm:flex items-center space-x-2 px-3 py-1 bg-blue-50 rounded-lg">
                  <MapPinIcon className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">{selectedLocation.name}</span>
                </div>
              )}
              
              {/* Business Type Toggle - Show on all screen sizes if location has both types */}
              {hasMultipleTypes && selectedLocation && (
                <div className="flex items-center space-x-1 sm:space-x-2 px-1 sm:px-2 py-1 bg-gray-100 rounded-lg">
                  <button
                    onClick={() => setBusinessType('fish')}
                    className={`px-2 sm:px-3 py-1 text-xs font-medium rounded transition-colors ${
                      businessType === 'fish'
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="hidden sm:inline">🐟 </span>Fish
                  </button>
                  <button
                    onClick={() => setBusinessType('goat')}
                    className={`px-2 sm:px-3 py-1 text-xs font-medium rounded transition-colors ${
                      businessType === 'goat'
                        ? 'bg-green-600 text-white'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="hidden sm:inline">🐐 </span>Goat
                  </button>
                </div>
              )}
              
              {/* Notifications */}
              {(user?.role === 'admin' || user?.role === 'manager') && (
                <Notifications />
              )}

              {/* Demo conversion CTA */}
              {isDemoUser && (
                <button
                  onClick={handleStartForFarm}
                  className="inline-flex items-center rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-amber-400 transition"
                >
                  Start for your farm
                </button>
              )}
              
              {/* User info */}
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white font-semibold text-sm">
                  {user?.full_name?.charAt(0)?.toUpperCase()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 min-h-screen">
          {!isInitialized || locationLoading ? (
            <div className="flex items-center justify-center min-h-screen">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your farm location...</p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </div>
  );
};

export default DashboardLayout;